import { FaMobile } from 'react-icons/fa';
import { FaCloud } from 'react-icons/fa';
import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaBookOpen,
  FaLaptopCode,
  FaFileAlt,
  FaUsers,
  FaLock,
  FaDatabase,
  FaUserGraduate,
  FaArrowUp,
  FaSearch,
  FaCertificate,
  FaBriefcase,
  FaEnvelope,
  FaStar,
  FaPlay,
  FaRegClock,
  FaChartLine
} from "react-icons/fa";
import { ReactTyped } from "react-typed";
import Slider from "react-slick";
import AOS from "aos";
import "aos/dist/aos.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import api from "../services/api";
import { useCounter } from '../hooks/useCounter';
import { useAuth } from '../contexts/AuthContext';
import PageHelmet from "../components/common/PageHelmet";
import Testimonials from "../components/home/Testimonials";

// --- COMPONENT: Animated Stat Card ---
const AnimatedStatCard = ({ icon: Icon, number, text, color }) => {
  const animatedNumber = useCounter(number);
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      style={{
        ...styles.statCard,
        transform: isHovered ? 'translateY(-10px) scale(1.05)' : 'translateY(0) scale(1)',
        boxShadow: isHovered ? '0 20px 40px rgba(0,0,0,0.15)' : '0 8px 25px rgba(0,0,0,0.1)',
        borderLeft: `5px solid ${color}`
      }}
      className="stat-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-aos="zoom-in"
    >
      <Icon style={{ fontSize: "40px", color, marginBottom: "12px" }} className="stat-icon"/>
      <h3 style={styles.statNumber}>
        {animatedNumber}{String(number).includes("+") ? "+" : ""}
      </h3>
      <p style={styles.statText}>{text}</p>
    </div>
  );
};

// --- Floating Particle Background Animation ---
const FloatingBackground = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if(!canvas) return;
    const ctx = canvas.getContext('2d');
    
    const setDimensions = () => {
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
    };
    setDimensions();
    
    const particles = [];
    const particleCount = window.innerWidth < 768 ? 15 : 30;
    
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 3 + 1,
        color: `rgba(79, 70, 229, ${Math.random() * 0.3})`,
        speedX: Math.random() * 0.5 - 0.25,
        speedY: Math.random() * 0.5 - 0.25,
      });
    }
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(particle => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;
        
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.fill();
      });
      
      requestAnimationFrame(animate);
    };
    
    animate();
    
    window.addEventListener('resize', setDimensions);
    return () => window.removeEventListener('resize', setDimensions);
  }, []);
  
  return <canvas ref={canvasRef} style={styles.floatingCanvas} />;
};

// --- ADVANCED FEATURE 1: Instant Search Component ---
const DynamicSearchBar = ({ navigate }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const fetchResults = async () => {
        if (searchQuery.length > 2) {
            setIsSearching(true);
            try {
                const response = await api.get(`/courses?search=${searchQuery}`);
                if (response && response.data) {
                    setSearchResults(response.data.slice(0, 5));
                }
            } catch (error) {
                console.error("Course search failed", error);
                setSearchResults([]);
            }
        } else {
            setSearchResults([]);
        }
    };

    const timer = setTimeout(fetchResults, 400); 
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleResultClick = (courseId) => {
    setSearchQuery('');
    setSearchResults([]);
    navigate(`/courses/${courseId}`);
  };

  return (
    <div style={styles.searchWrapper} data-aos="fade-up" data-aos-delay="600" className="hero-search-wrapper">
      <form 
        onSubmit={(e) => {
          e.preventDefault();
          if (searchQuery.length > 0) {
            navigate(`/courses?search=${searchQuery}`);
          }
        }} 
        style={styles.searchContainer}
        className="search-form-container"
      >
        <FaSearch style={styles.searchIcon} />
        <input
          type="text"
          placeholder="🔍 Search courses..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
          className="hero-search-input"
          onFocus={() => setIsSearching(true)}
          onBlur={() => setTimeout(() => setIsSearching(false), 200)}
        />
        <button type="submit" style={styles.searchButton} className="hero-search-btn">
          Search
        </button>
      </form>
      
      {searchResults.length > 0 && isSearching && (
        <div style={styles.searchResultsDropdown} className="search-results-dropdown">
          {searchResults.map((course, index) => (
            <div 
              key={course.id} 
              style={{
                ...styles.searchResultItem,
                animationDelay: `${index * 0.1}s`
              }}
              onClick={() => handleResultClick(course.id)}
              className="search-result-item"
            >
              <div style={styles.courseIcon}>📚</div>
              <div style={styles.courseInfo}>
                <div style={styles.courseName}>{course.title}</div>
                <div style={styles.courseMeta}>
                  <span style={styles.courseCategory}>{course.category}</span>
                  <span style={styles.courseRating}>
                    <FaStar style={{ color: '#fbbf24', marginRight: '4px' }} />
                    {course.rating || '4.5'}
                  </span>
                </div>
              </div>
              <FaPlay style={styles.resultArrow} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Hoisted out of the component: as a literal inside the render it was a new
// array every time, so the auto-rotate effect below could not depend on it
// without resetting its interval on every render.
const COURSE_CATEGORIES = ["All", "Web Dev", "Data Science", "Security", "Infrastructure"];

// --- ADVANCED FEATURE 2: 3D Rotating Course Carousel ---
const FeaturedCourses = () => {
  const [filter, setFilter] = useState("All");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [courses, setCourses] = useState([]);
  // Only the setter is used; the flag itself is never read.
  const [, setLoading] = useState(true);
  const sliderRef = useRef(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await api.get('/courses?limit=6');
        if (response.data && response.data.success) {
          const courseData = response.data.data || response.data.courses || [];
          const transformedCourses = courseData.map((course, index) => ({
            id: course.id || course._id,
            title: course.title || course.name,
            category: course.category || course.subject || "General",
            image: getCourseEmoji(course.category || course.subject),
            duration: course.duration || "8 weeks",
            level: course.level || course.difficulty || "Beginner",
            // Only a real rating, if the API supplies one. This used to default
            // to 4.5, so every course on the homepage showed 4.5 stars whether
            // or not anyone had rated it.
            rating: course.rating ?? course.averageRating ?? null,
            color: getCategoryColor(course.category || course.subject, index)
          }));
          setCourses(transformedCourses);
        }
      } catch (error) {
        // Show nothing rather than inventing courses. The old fallback seeded
        // three fictional courses ("Full Stack Web Dev", 4.9★) that never
        // existed in the catalogue.
        console.error('Error fetching courses:', error);
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const getCourseEmoji = (category) => {
    const emojiMap = {
      "Web Dev": "💻", "Data Science": "📊", "Security": "🛡️", "AI": "🤖",
      "Mobile": "📱", "Cloud": "☁️", "Database": "🗄️", "DevOps": "⚙️"
    };
    return emojiMap[category] || "📚";
  };

  const getCategoryColor = (category, index) => {
    const colorMap = {
      "Web Dev": "#3B82F6", "Data Science": "#8B5CF6", "Security": "#10B981",
      "AI": "#EF4444", "Mobile": "#F59E0B", "Cloud": "#06B6D4"
    };
    const colors = ["#3B82F6", "#8B5CF6", "#10B981", "#EF4444", "#F59E0B", "#06B6D4"];
    return colorMap[category] || colors[index % colors.length];
  };

  const filteredCourses = useMemo(() => {
    if (filter === "All") return courses;
    return courses.filter(c => c.category === filter);
  }, [filter, courses]);

  const sliderSettings = {
    ref: sliderRef,
    dots: true,
    infinite: true,
    speed: 600,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    centerMode: true,
    centerPadding: "0px",
    beforeChange: (current, next) => setActiveIndex(next),
    responsive: [
      { breakpoint: 1200, settings: { slidesToShow: 2, centerMode: false } },
      { breakpoint: 768, settings: { slidesToShow: 1, centerMode: false, dots: false } },
      { breakpoint: 480, settings: { slidesToShow: 1, centerMode: false, dots: false, arrows: false } }
    ]
  };

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      const currentIndex = COURSE_CATEGORIES.indexOf(filter);
      const nextIndex = (currentIndex + 1) % COURSE_CATEGORIES.length;
      setFilter(COURSE_CATEGORIES[nextIndex]);
    }, 5000);
    return () => clearInterval(interval);
  }, [filter, isPaused]);

  return (
    <section style={styles.categorySection} data-aos="fade-up" data-aos-delay="200"
      onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}
      className="featured-section">
      <div style={styles.sectionHeader} className="section-header">
        <h2 style={styles.sectionTitle} className="section-title">Featured Courses</h2>
      </div>
      
      <div style={styles.filterTabs} className="filter-tabs">
        {COURSE_CATEGORIES.map((cat) => (
          <button key={cat} onClick={() => setFilter(cat)}
            style={{
              ...styles.filterTab,
              background: filter === cat ? 'linear-gradient(135deg, #4F46E5, #7C3AED)' : 'transparent',
              color: filter === cat ? 'white' : '#6B7280',
              borderColor: filter === cat ? '#4F46E5' : '#E5E7EB',
            }}
            className="filter-tab">
            {cat}
            {filter === cat && <span style={styles.activeDot}>●</span>}
          </button>
        ))}
      </div>

      <div style={styles.courseCount}>
        Showing {filteredCourses.length} of {courses.length} courses
      </div>

      <div style={{ position: 'relative', marginTop: '40px' }} className="course-slider-container">
        <Slider {...sliderSettings}>
          {filteredCourses.map((course, index) => (
            <div key={course.id} style={{ padding: "0 15px" }} className="slick-slide-inner">
              <div style={{
                ...styles.courseCard,
                transform: activeIndex === index ? 'scale(1.05)' : 'scale(1)',
                opacity: activeIndex === index ? 1 : 0.9,
                borderTop: `4px solid ${course.color}`,
                boxShadow: activeIndex === index ? `0 20px 40px ${course.color}40` : '0 8px 25px rgba(0,0,0,0.1)',
              }} className="course-card">
                <div style={styles.courseBadge}>🔥 Trending</div>
                <div style={{ fontSize: "50px", marginBottom: "12px", filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))' }}>
                  {course.image}
                </div>
                <h3 style={styles.courseTitle}>{course.title}</h3>
                <span style={{ ...styles.categoryBadge, backgroundColor: `${course.color}20`, color: course.color }}>
                  {course.category}
                </span>
                <div style={styles.courseMeta} className="course-meta">
                  <span style={styles.metaItem}><FaRegClock style={{ marginRight: '5px' }} />{course.duration}</span>
                  <span style={styles.metaItem}><FaUserGraduate style={{ marginRight: '5px' }} />{course.level}</span>
                  {course.rating > 0 && (
                    <span style={styles.metaItem}><FaStar style={{ color: '#FBBF24', marginRight: '5px' }} />{Number(course.rating).toFixed(1)}</span>
                  )}
                </div>
                <button style={styles.viewCourseButton} className="view-course-btn">
                  View Course →
                </button>
              </div>
            </div>
          ))}
        </Slider>
        
        <div style={styles.carouselControls} className="carousel-controls">
          <button onClick={() => sliderRef.current?.slickPrev()} style={styles.controlButton}>◀</button>
          <button onClick={() => sliderRef.current?.slickNext()} style={styles.controlButton}>▶</button>
        </div>
      </div>
      
      <div style={styles.progressIndicator}>
        {filteredCourses.map((_, idx) => (
          <div key={idx} style={{
            ...styles.progressDot,
            width: activeIndex === idx ? '20px' : '8px',
            backgroundColor: activeIndex === idx ? '#4F46E5' : '#CBD5E1'
          }} />
        ))}
      </div>
    </section>
  );
};

// --- Animated Progress Bar Component ---
const ProgressBar = ({ percentage, label }) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setWidth(percentage), 300);
    return () => clearTimeout(timer);
  }, [percentage]);

  return (
    <div style={styles.progressBarContainer} className="progress-bar-container">
      <div style={styles.progressBarLabel}>
        <span>{label}</span>
        <span>{percentage}%</span>
      </div>
      <div style={styles.progressBarTrack}>
        <div style={{
          ...styles.progressBarFill,
          width: `${width}%`,
          background: 'linear-gradient(90deg, #4F46E5, #7C3AED)'
        }} />
      </div>
    </div>
  );
};

// --- Learning Journey Component ---
const LearningJourney = ({ user }) => {
  const [progressData, setProgressData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgressData = async () => {
      if (!user) { setLoading(false); return; }
      try {
        setLoading(true);
        const response = await api.get('/analytics/student/progress');
        if (response.data && response.data.success) {
          const data = response.data.data || {};
          const transformedData = [
            { label: "Web Development", percentage: data.webDevProgress || 0 },
            { label: "Data Structures", percentage: data.dataStructuresProgress || 0 },
            { label: "Cyber Security", percentage: data.cyberSecurityProgress || 0 },
            { label: "Machine Learning", percentage: data.machineLearningProgress || 0 }
          ];
          setProgressData(transformedData);
        }
      } catch (error) {
        console.error('Error fetching progress data:', error);
        setProgressData([
          { label: "Web Development", percentage: 85 },
          { label: "Data Structures", percentage: 70 },
          { label: "Cyber Security", percentage: 60 },
          { label: "Machine Learning", percentage: 45 }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchProgressData();
  }, [user]);

  if (!user) return null;

  if (loading) {
    return (
      <section style={styles.progressSection} data-aos="fade-up" className="progress-section">
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle} className="section-title">Your Learning Journey</h2>
          <p style={styles.sectionSubtitle} className="section-subtitle">Track progress across different skill domains</p>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading your progress...</div>
      </section>
    );
  }

  return (
    <section style={styles.progressSection} data-aos="fade-up" className="progress-section">
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle} className="section-title">Your Learning Journey</h2>
        <p style={styles.sectionSubtitle} className="section-subtitle">Track progress across different skill domains</p>
      </div>
      <div style={styles.progressGrid} className="progress-grid">
        {progressData.map((item, index) => (
          <ProgressBar key={index} percentage={item.percentage} label={item.label} />
        ))}
      </div>
    </section>
  );
};

function Home() {
  const { user } = useAuth();
  const [showScroll, setShowScroll] = useState(false);
  const [email, setEmail] = useState("");
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  // Real platform counts. The stats strip and hero used to hardcode
  // "1,200+ Active Students", "80+ Live Projects", "4.8/5 rating" and so on —
  // none of which came from the data. On a young platform that's simply false,
  // so the figures now come from the public catalogue endpoints, and anything
  // that couldn't be sourced honestly (student totals, an aggregate rating,
  // placement counts) was replaced with a capability the platform actually has.
  const [counts, setCounts] = useState({});
  useEffect(() => {
    let alive = true;
    const num = (r) => {
      const d = r?.value?.data;
      if (!d) return undefined;
      if (typeof d?.pagination?.total === "number") return d.pagination.total;
      if (Array.isArray(d?.data)) return d.data.length;
      if (Array.isArray(d)) return d.length;
      return undefined;
    };
    Promise.allSettled([
      api.get("/courses?limit=1"),
      api.get("/projects"),
      api.get("/pyq?limit=1"),
      api.get("/internship"),
    ]).then(([c, p, q, i]) => {
      if (!alive) return;
      setCounts({
        courses: num(c),
        projects: num(p),
        papers: num(q),
        internships: num(i),
      });
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true, offset: 50 });
    
    const handleScroll = () => setShowScroll(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    
    const handleMouseMove = (e) => {
       if (window.matchMedia("(hover: hover)").matches) {
           setMousePosition({ x: e.clientX, y: e.clientY });
       }
    };
    window.addEventListener("mousemove", handleMouseMove);
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Newsletter signup. This used to alert a thank-you and throw the address
  // away; it now stores it through POST /api/newsletter/subscribe.
  const [subscribeState, setSubscribeState] = useState({ status: "idle", message: "" });

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const address = email.trim();
    if (!address || subscribeState.status === "loading") return;

    setSubscribeState({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email: address,
        source: "home",
      });
      setSubscribeState({
        status: "success",
        message: data?.message || "Subscribed.",
      });
      setEmail("");
    } catch (err) {
      setSubscribeState({
        status: "error",
        message:
          err.response?.data?.message ||
          "Could not subscribe right now. Please try again.",
      });
    }
  };

  return (
    <div style={styles.container} className="home-container">
      <PageHelmet
        title="Online CS & IT Courses and Internships for AKTU, B.Tech, BCA, MCA & MTech"
        description="ByteGurukul offers online tech courses, beginner tech internships, and real-world projects for AKTU, B.Tech, M.Tech, BCA, MCA, B.Sc, diploma M.Tech students and freshers. Learn with hands-on training and build job-ready skills."
        robots="index,follow"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "ByteGurukul",
            "url": "https://www.bytegurukul.tech/",
            "logo": "https://www.bytegurukul.tech/logo192.png",
            "sameAs": [],
            "description": "ByteGurukul provides online tech courses, internships and real-world projects for students and freshers.",
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "ByteGurukul",
            "url": "https://www.bytegurukul.tech/",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.bytegurukul.tech/courses?search={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.bytegurukul.tech/",
              },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Who can join ByteGurukul online internships?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "AKTU students and every computer science and IT student — B.Tech, M.Tech, BCA, MCA, B.Sc and diploma — plus freshers can apply for ByteGurukul online internships and beginner tech internships.",
                },
              },
              {
                "@type": "Question",
                "name": "What kind of projects will I work on at ByteGurukul?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You work on guided, real-world projects in web development, cybersecurity, data science and more so that your internship and course work directly improves your job portfolio.",
                },
              },
              {
                "@type": "Question",
                "name": "Are ByteGurukul online tech courses beginner friendly?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Our online tech courses start from the basics and use hands-on training so beginners and freshers can quickly build confidence and practical skills.",
                },
              },
            ],
          },
        ]}
      />
      {/* Mouse Trailer Effect */}
      <div 
        style={{
          ...styles.mouseTrailer,
          left: mousePosition.x,
          top: mousePosition.y,
        }}
        className="mouse-trailer"
      />
      
      {/* HERO SECTION */}
      <section style={styles.hero} className="hero-section">
        <FloatingBackground />
        <div style={styles.heroOverlay} />
        
        <div style={styles.heroContent} className="hero-content">
          <div style={styles.heroLeft} className="hero-left">
            <h1 style={styles.heroTitle} data-aos="fade-right" data-aos-delay="100" className="hero-title">
              Welcome to <span style={styles.gradientText}>ByteGurukul</span>
            </h1>
            
            <h3 style={styles.heroTagline} data-aos="fade-right" data-aos-delay="200" className="hero-tagline">
              <ReactTyped
                strings={[
                  "Learn. Build. Excel.",
                  "Empowering AKTU & CS/IT Students.",
                  "Learn Smart — Learn Secure.",
                  "Your Path to Tech Excellence."
                ]}
                typeSpeed={50}
                backSpeed={40}
                loop
                showCursor={true}
                cursorChar="|"
              />
            </h3>
            
            <p style={styles.heroSubtitle} data-aos="fade-right" data-aos-delay="300" className="hero-subtitle">
              Your complete learning platform for <strong>AKTU &amp; CS/IT students</strong> &{" "}
              <strong>M.Tech</strong> Computer Science. Access courses, projects,
              internships, and previous year questions — all in one place.
            </p>
            
            <DynamicSearchBar navigate={navigate} />

            <div style={styles.heroButtons} data-aos="fade-up" data-aos-delay="400" className="hero-buttons">
              <Link to="/courses" style={styles.primaryButton} className="hero-btn-primary">
                <FaLaptopCode style={{ marginRight: '10px' }} />
                Explore Courses
              </Link>
              {!user && (
                <Link to="/signup" style={styles.secondaryButton} className="hero-btn-secondary">
                  <FaUserGraduate style={{ marginRight: '10px' }} />
                  Sign Up Free
                </Link>
              )}
            </div>
            
            {/* Capabilities the platform genuinely offers, rather than
                invented social-proof numbers. */}
            <div style={styles.trustIndicators} data-aos="fade-up" data-aos-delay="500" className="hero-trust">
              <div style={styles.trustItem} className="trust-item">
                <FaFileAlt style={{ color: '#10B981' }} />
                <span>Free previous year papers</span>
              </div>
              <div style={styles.trustItem} className="trust-item">
                <FaCertificate style={{ color: '#FBBF24' }} />
                <span>Verifiable certificates</span>
              </div>
              <div style={styles.trustItem} className="trust-item">
                <FaBriefcase style={{ color: '#3B82F6' }} />
                <span>Remote internships</span>
              </div>
            </div>
          </div>

          <div style={styles.heroRight} data-aos="fade-left" data-aos-delay="300" className="hero-right">
            <div style={styles.heroImageContainer} className="hero-img-container">
              <img
                src="/hero-learning.png"
                alt="Students learning online at ByteGurukul"
                style={styles.heroImage}
                className="floating-image"
              />
              <div style={styles.floatingElement1} className="floating-el">🎯</div>
              <div style={styles.floatingElement2} className="floating-el">🏆</div>
              <div style={styles.floatingElement3} className="floating-el">💡</div>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK STATS — real counts from the catalogue. Any category with
          nothing in it yet is left out rather than shown as a zero. */}
      {(() => {
        const statItems = [
          counts.courses > 0 && { key: "c", icon: FaBookOpen, number: counts.courses, text: counts.courses === 1 ? "Course" : "Courses", color: "#3B82F6" },
          counts.projects > 0 && { key: "p", icon: FaLaptopCode, number: counts.projects, text: counts.projects === 1 ? "Project" : "Projects", color: "#10B981" },
          counts.papers > 0 && { key: "q", icon: FaFileAlt, number: counts.papers, text: "Previous year papers", color: "#8B5CF6" },
          counts.internships > 0 && { key: "i", icon: FaBriefcase, number: counts.internships, text: counts.internships === 1 ? "Internship role" : "Internship roles", color: "#EF4444" },
        ].filter(Boolean);
        if (statItems.length === 0) return null;
        return (
          <section style={styles.statsSection} data-aos="fade-up" className="stats-section">
            {statItems.map((s) => (
              <AnimatedStatCard key={s.key} icon={s.icon} number={String(s.number)} text={s.text} color={s.color} />
            ))}
          </section>
        );
      })()}

      {/* TRUST SECTION */}
      <section style={styles.trustSection} data-aos="fade-up" className="trust-section">
        <div style={styles.sectionHeader} className="section-header">
          <h2 style={styles.sectionTitle} className="section-title">Built for AKTU &amp; CS/IT Students, By Experts</h2>
          <p style={styles.sectionSubtitle} className="section-subtitle">Everything you need to excel in your CS journey</p>
        </div>
        <div style={styles.trustGrid} className="trust-grid">
          {[
            { icon: FaCertificate, title: "Verified Certification", desc: "Earn industry-recognized certificates upon course completion.", color: "#3B82F6" },
            { icon: FaBriefcase, title: "Internship Opportunities", desc: "Direct access to hands-on, real-world virtual internships.", color: "#8B5CF6" },
            { icon: FaUsers, title: "Community Support", desc: "24/7 forum access and mentorship from experienced peers.", color: "#10B981" },
            { icon: FaChartLine, title: "Progress Tracking", desc: "Detailed analytics and progress reports for each student.", color: "#F59E0B" },
          ].map((item, index) => (
            <div key={index} style={{ ...styles.trustCard, borderTop: `4px solid ${item.color}` }}
              data-aos="zoom-in" data-aos-delay={index * 100} className="trust-card">
              <item.icon style={{ fontSize: "40px", color: item.color, marginBottom: "15px" }} />
              <h3 style={styles.trustCardTitle}>{item.title}</h3>
              <p style={styles.trustCardDesc}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>
      
      <FeaturedCourses />

      <LearningJourney user={user} />

      {/* COURSE CATEGORIES */}
      <section style={styles.categorySection} data-aos="fade-up" className="category-section">
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle} className="section-title">Explore Categories</h2>
          <p style={styles.sectionSubtitle} className="section-subtitle">Specialized paths for every interest</p>
        </div>
        <div style={styles.categoryGrid} className="category-grid">
          {/* Category blurbs rather than invented per-category course counts
              (they summed to 100+ against a catalogue of a handful). */}
          {[
            { icon: FaLaptopCode, title: "Web Development", blurb: "Frontend, backend and full-stack builds", color: "#3B82F6" },
            { icon: FaLock, title: "Cyber Security", blurb: "Tooling, analysis and secure coding", color: "#10B981" },
            { icon: FaDatabase, title: "Data Science", blurb: "Analysis, models and reproducible notebooks", color: "#8B5CF6" },
            { icon: FaChartLine, title: "AI & Machine Learning", blurb: "Training, evaluation and applied ML", color: "#EF4444" },
            { icon: FaMobile, title: "Mobile Development", blurb: "Android and cross-platform apps", color: "#F59E0B" },
            { icon: FaCloud, title: "Cloud Computing", blurb: "Deployment, DevOps and infrastructure", color: "#06B6D4" },
          ].map((cat, index) => (
            <div key={index} style={{
                ...styles.categoryCard,
                background: `linear-gradient(135deg, ${cat.color}20, ${cat.color}10)`,
                border: `2px solid ${cat.color}30`,
              }}
              data-aos="fade-up" data-aos-delay={index * 100} className="category-card">
              <cat.icon style={{ fontSize: "40px", color: cat.color, marginBottom: "12px" }} />
              <h3 style={styles.categoryTitle}>{cat.title}</h3>
              <p style={styles.categoryCourses}>{cat.blurb}</p>
              <div style={styles.categoryHover} className="category-hover">Explore →</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE BYTEGURUKUL */}
      <section style={styles.features} data-aos="fade-up" className="features-section">
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle} className="section-title">Why Choose ByteGurukul?</h2>
          <p style={styles.sectionSubtitle} className="section-subtitle">Comprehensive platform for complete CS education</p>
        </div>
        <div style={styles.featuresGrid} className="features-grid">
          <Link to="/pyq-papers" style={styles.featureLink}>
            <div style={styles.featureCard} className="feature-card">
              <div style={styles.featureIconContainer}>
                <FaFileAlt style={styles.featureIcon} />
              </div>
              <h3 style={styles.featureTitle}>PYQ Papers</h3>
              <p style={styles.featureDesc}>Access previous year question papers with detailed solutions and explanations.</p>
              <div style={styles.featureStats}>Free to open</div>
            </div>
          </Link>
          <div style={styles.featureCard} className="feature-card">
            <div style={styles.featureIconContainer}>
              <FaBookOpen style={styles.featureIcon} />
            </div>
            <h3 style={styles.featureTitle}>Study Resources</h3>
            <p style={styles.featureDesc}>Download notes, books, and presentations for all CS courses.</p>
            <div style={styles.featureStats}>Semester-mapped</div>
          </div>
          <div style={styles.featureCard} className="feature-card">
            <div style={styles.featureIconContainer}>
              <FaLaptopCode style={styles.featureIcon} />
            </div>
            <h3 style={styles.featureTitle}>Domain Projects</h3>
            <p style={styles.featureDesc}>Purchase ready-to-use projects categorized by domains with source code.</p>
            <div style={styles.featureStats}>With source code</div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER SECTION */}
      <section style={styles.newsletterSection} data-aos="fade-up" className="newsletter-section">
        <div style={styles.newsletterContent} className="newsletter-content">
          <FaEnvelope style={styles.newsletterIcon} />
          <h2 style={styles.newsletterTitle} className="newsletter-title">Stay Ahead of the Curve</h2>
          <p style={styles.newsletterText} className="newsletter-text">
            Get the latest course updates, internship alerts, and tech news delivered to your inbox.
          </p>
          <form onSubmit={handleSubscribe} style={styles.newsletterForm} className="newsletter-form">
            <input 
              type="email" 
              placeholder="Enter your email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.newsletterInput}
              className="newsletter-input"
              required
            />
            <button
              type="submit"
              style={styles.newsletterButton}
              className="newsletter-btn"
              disabled={subscribeState.status === "loading"}
            >
              {subscribeState.status === "loading" ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
          {subscribeState.message ? (
            <p
              role="status"
              style={{
                ...styles.newsletterDisclaimer,
                color: subscribeState.status === "error" ? "#FCA5A5" : "#A7F3D0",
                fontWeight: 600,
              }}
            >
              {subscribeState.message}
            </p>
          ) : (
            <p style={styles.newsletterDisclaimer}>No spam. Unsubscribe anytime.</p>
          )}
        </div>
      </section>
      
      {/* ABOUT SECTION */}
      <section style={styles.about} data-aos="fade-up" className="about-section">
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle} className="section-title">About the Creator</h2>
          <p style={styles.sectionSubtitle} className="section-subtitle">Passionate educator building the future of CS learning</p>
        </div>
        <div style={styles.aboutContainer} className="about-container">
          <div style={styles.aboutImageContainer} className="about-image-wrapper">
            <img
              src="/abhijeet.jpg"
              alt="Abhijeet Kumar Pandey, creator of ByteGurukul"
              loading="lazy"
              style={styles.aboutImage}
              className="about-image"
            />
            <div style={styles.aboutBadge}>Creator</div>
          </div>
          <div style={styles.aboutContent} className="about-content">
            <h3 style={styles.aboutName} className="about-name">Abhijeet Kumar Pandey</h3>
            <p style={styles.aboutRole}>Cyber Security Analyst | M.Tech (CSE)</p>
            <p style={styles.aboutBio}>
              I'm passionate about technology, cybersecurity, and education.
              ByteGurukul is my initiative to make learning accessible for AKTU and other computer science and IT
              B.Tech & M.Tech students — providing quality study materials,
              projects, and guidance for everyone who dreams big.
            </p>
            <div style={styles.aboutStats} className="about-stats">
              <div style={styles.stat}>
                <span style={styles.aboutStatNumber}>3+</span>
                <span style={styles.statLabel}>Years Experience</span>
              </div>
              <div style={styles.stat}>
                <span style={styles.aboutStatNumber}>500+</span>
                <span style={styles.statLabel}>Students Mentored</span>
              </div>
              <div style={styles.stat}>
                <span style={styles.aboutStatNumber}>10+</span>
                <span style={styles.statLabel}>Projects Built</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      {/* Real course reviews; renders nothing until there are some. */}
      <Testimonials />

      <section style={styles.faqSection} data-aos="fade-up" className="faq-section">
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle} className="section-title">Frequently Asked Questions</h2>
          <p style={styles.sectionSubtitle} className="section-subtitle">
            Answers for AKTU, B.Tech, M.Tech, BCA, MCA, B.Sc and diploma students exploring ByteGurukul online tech courses and internships.
          </p>
        </div>
        <div style={styles.faqGrid} className="faq-grid">
          <div style={styles.faqItem}>
            <h3 style={styles.faqQuestion}>Who can apply for ByteGurukul online internships?</h3>
            <p style={styles.faqAnswer}>
              Our online internships are designed for AKTU students and every computer science and IT student — B.Tech, M.Tech, BCA, MCA, B.Sc and diploma — as well as freshers who want an internship for students with guided support.
            </p>
          </div>
          <div style={styles.faqItem}>
            <h3 style={styles.faqQuestion}>What kind of work will I do during an internship?</h3>
            <p style={styles.faqAnswer}>
              You work on beginner-friendly, real-world projects in web development, cybersecurity, data science and more so that your portfolio reflects practical, hands-on training.
            </p>
          </div>
          <div style={styles.faqItem}>
            <h3 style={styles.faqQuestion}>Are ByteGurukul courses suitable for beginners?</h3>
            <p style={styles.faqAnswer}>
              Yes. Each online tech course starts from the basics and gradually moves to advanced topics with projects, so even freshers and non-CS backgrounds can follow along.
            </p>
          </div>
          <div style={styles.faqItem}>
            <h3 style={styles.faqQuestion}>Do I receive certificates or experience letters?</h3>
            <p style={styles.faqAnswer}>
              On successfully completing eligible courses and online internships, you receive ByteGurukul certificates and internship completion letters that showcase your skills to recruiters.
            </p>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section style={styles.ctaSection} data-aos="fade-up" className="cta-section">
        <div style={styles.ctaContent} className="cta-content">
          <h2 style={styles.ctaTitle} className="cta-title">Start Your Learning Journey Today!</h2>
          <p style={styles.ctaText} className="cta-text">
            Join thousands of AKTU and CS/IT students who are already advancing their careers with ByteGurukul.
          </p>
          <div style={styles.ctaButtons} className="cta-buttons">
            {!user && (
              <Link to="/signup" style={styles.ctaPrimaryButton} className="cta-btn-primary">
                Get Started Free
              </Link>
            )}
            <Link to="/courses" style={styles.ctaSecondaryButton} className="cta-btn-secondary">
              Browse Courses
            </Link>
          </div>
        </div>
      </section>

      {/* SCROLL TO TOP */}
      {showScroll && (
        <button onClick={scrollToTop} style={styles.scrollButton} className="scroll-button">
          <FaArrowUp />
        </button>
      )}

      {/* GLOBAL RESPONSIVE STYLES */}
      <style>
        {`
          @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-20px); } }
          @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
          @keyframes slideIn { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

          .floating-image { animation: float 6s ease-in-out infinite; }
          .scroll-button { animation: pulse 2s infinite; }
          .search-result-item { animation: slideIn 0.3s ease-out forwards; opacity: 0; }

          @media (hover: hover) and (pointer: fine) {
            .course-card:hover { transform: translateY(-10px) scale(1.03) !important; transition: all 0.4s ease !important; }
            .category-card:hover { transform: translateY(-8px) !important; box-shadow: 0 20px 40px rgba(79, 70, 229, 0.15) !important; }
            .category-card:hover .category-hover { opacity: 1 !important; transform: translateY(0) !important; }
            .feature-card:hover { transform: translateY(-10px); box-shadow: 0 20px 40px rgba(79, 70, 229, 0.15); border-color: #4F46E5 !important; }
            .about-image:hover { transform: scale(1.05); }
            .hero-btn-primary:hover, .cta-btn-primary:hover { transform: translateY(-3px); box-shadow: 0 15px 30px rgba(79, 70, 229, 0.4); }
            .hero-btn-secondary:hover, .cta-btn-secondary:hover { background-color: rgba(255, 255, 255, 0.2) !important; }
          }

          /* Extra Small: 320px - 480px */
          @media (max-width: 480px) {
            /* column-reverse makes align-items the horizontal axis, so the
               inline "center" sizes children to content and they outgrow
               the viewport. Stretch instead. */
            .hero-content { flex-direction: column-reverse; align-items: stretch !important; text-align: center; padding: 0 16px; }
            .hero-left { text-align: center; width: 100%; min-width: 0 !important; }
            .hero-right { width: 100%; min-width: 0 !important; margin-bottom: 28px; }
            .hero-title { font-size: 32px !important; line-height: 1.2; }
            .hero-tagline { font-size: 18px !important; min-height: 60px; }
            .hero-subtitle { font-size: 15px !important; margin-bottom: 30px !important; }
            .hero-buttons { flex-direction: column; gap: 12px; }
            .hero-btn-primary, .hero-btn-secondary { width: 100%; padding: 14px !important; font-size: 15px !important; justify-content: center; }
            /* the form renders as .search-form-container — the old
               .search-container selector never matched anything */
            .search-form-container { flex-direction: column; }
            .hero-search-input { min-width: 0; width: 100%; }
            .search-icon { padding: 12px; justify-content: center; }
            .search-button { width: 100%; padding: 14px !important; border-radius: 0 0 14px 14px; }
            .hero-trust { flex-direction: column; align-items: center; gap: 10px; }
            .trust-item { font-size: 13px; }
            .section-title { font-size: 26px !important; }
            .stat-card { padding: 20px 16px !important; }
            /* Comfortable touch targets on phones */
            .filter-tab { min-height: 40px; }
            .view-course-btn { min-height: 44px; }
            .newsletter-form { flex-direction: column; gap: 12px; }
            .newsletter-input, .newsletter-btn { width: 100%; }
            .cta-buttons { flex-direction: column; gap: 14px; }
            .cta-btn-primary, .cta-btn-secondary { width: 100%; padding: 16px !important; }
            .floating-el, .mouse-trailer { display: none !important; }
          }

          /* Small Tablets & Large Phones: 481px - 767px */
          @media (min-width: 481px) and (max-width: 767px) {
            .hero-title { font-size: 38px !important; }
            .hero-content { flex-direction: column-reverse; align-items: stretch !important; text-align: center; padding: 0 20px; }
            .hero-left { text-align: center; width: 100%; min-width: 0 !important; }
            .hero-right { width: 100%; min-width: 0 !important; margin-bottom: 32px; }
            .hero-buttons { gap: 16px; }
            .hero-trust { justify-content: center; flex-wrap: wrap; }
            .filter-tab { min-height: 40px; }
            .view-course-btn { min-height: 44px; }
          }

          /* Tablets: 768px - 1024px */
          @media (min-width: 768px) and (max-width: 1024px) {
            .hero-content { flex-direction: column-reverse; align-items: stretch !important; text-align: center; padding: 0 40px; }
            .hero-left { text-align: center; min-width: 0 !important; }
            .hero-right { margin-bottom: 40px; min-width: 0 !important; }
            .hero-search-wrapper { margin: 0 auto 40px auto; max-width: 600px; }
            .hero-buttons { justify-content: center; }
            .hero-trust { justify-content: center; }
            .stats-section { grid-template-columns: repeat(2, 1fr); }
            .trust-grid, .features-grid, .category-grid { grid-template-columns: repeat(2, 1fr); gap: 20px; }
            .about-container { flex-direction: column; text-align: center; }
            .about-content { text-align: center; }
            .about-stats { justify-content: center; }
          }

          /* Large Desktops: 1441px+ (Refined so it doesn't look overly zoomed in) */
          @media (min-width: 1441px) {
            .home-container { max-width: 1440px; margin: 0 auto; padding: 0 20px; }
            .hero-content { max-width: 1440px; }
            .hero-title { font-size: 58px !important; }
            .section-title { font-size: 42px !important; }
            .course-card { padding: 35px 25px !important; }
            .feature-card { padding: 40px 35px !important; }
            .category-card { padding: 35px 25px !important; }
          }

          img, canvas, .hero-img-container, .search-form-container { max-width: 100% !important; }
          img, canvas { height: auto; }
        `}
      </style>
    </div>
  );
}

/* ---------------------------- STYLES OBJECT ---------------------------- */
const styles = {
  container: {
    backgroundColor: "#F8FAFC",
    color: "#1E293B",
    fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    overflowX: "hidden",
    position: "relative",
    width: "100%",
    maxWidth: "100vw",
  },

  mouseTrailer: {
    position: "fixed",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(79, 70, 229, 0.2) 0%, transparent 70%)",
    pointerEvents: "none",
    zIndex: 9999,
    transform: "translate(-50%, -50%)",
    transition: "left 0.05s ease-out, top 0.05s ease-out",
  },

  floatingCanvas: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 1,
  },

  hero: {
    position: "relative",
    padding: "clamp(50px, 8vw, 100px) 5%",
    background: "linear-gradient(135deg, #1E1B4B 0%, #312E81 50%, #4338CA 100%)",
    color: "white",
    overflow: "hidden",
  },
  heroOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "radial-gradient(circle at 20% 50%, rgba(79, 70, 229, 0.2) 0%, transparent 50%)",
    zIndex: 2,
  },
  heroContent: {
    position: "relative",
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    maxWidth: "1400px",
    margin: "0 auto",
    gap: "40px",
    width: "100%",
    padding: "0 20px",
  },
  heroLeft: {
    flex: "1 1 500px",
    minWidth: "280px",
  },
  heroRight: {
    flex: "1 1 400px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    maxWidth: "100%",
  },
  heroImageContainer: {
    position: "relative",
    maxWidth: "100%",
    width: "100%",
  },
  heroImage: {
    width: "100%",
    maxWidth: "550px",
    borderRadius: "24px",
    boxShadow: "0 32px 64px rgba(0, 0, 0, 0.3)",
    border: "8px solid rgba(255, 255, 255, 0.1)",
    height: "auto",
  },
  floatingElement1: { position: "absolute", top: "-20px", right: "-20px", fontSize: "clamp(20px, 3vw, 36px)", animation: "float 4s ease-in-out infinite" },
  floatingElement2: { position: "absolute", bottom: "40px", left: "-40px", fontSize: "clamp(18px, 2.5vw, 28px)", animation: "float 5s ease-in-out infinite 1s" },
  floatingElement3: { position: "absolute", top: "40px", right: "60px", fontSize: "clamp(16px, 2.5vw, 24px)", animation: "float 6s ease-in-out infinite 2s" },

  gradientText: {
    background: "linear-gradient(135deg, #60A5FA 0%, #A78BFA 50%, #F472B6 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },

  heroTitle: { fontSize: "clamp(32px, 5vw, 54px)", fontWeight: "800", marginBottom: "16px", lineHeight: "1.2" },
  heroTagline: { fontSize: "clamp(18px, 2.5vw, 24px)", fontWeight: "600", marginBottom: "24px", color: "#C7D2FE", minHeight: "36px" },
  heroSubtitle: { fontSize: "clamp(15px, 1.5vw, 18px)", lineHeight: "1.6", marginBottom: "32px", color: "#E2E8F0", maxWidth: "550px" },

  searchWrapper: { position: "relative", maxWidth: "650px", margin: "30px auto", width: "100%" },
  searchContainer: { display: "flex", backgroundColor: "white", borderRadius: "14px", overflow: "hidden", boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)", border: "2px solid rgba(255, 255, 255, 0.2)", width: "100%" },
  searchIcon: { padding: "0 15px", color: "#64748B", fontSize: "18px", display: "flex", alignItems: "center" },
  searchInput: { flex: 1, minWidth: 0, padding: "14px 10px", border: "none", fontSize: "16px", outline: "none", color: "#1E293B" },
  searchButton: { padding: "0 20px", backgroundColor: "#4F46E5", color: "white", border: "none", fontSize: "15px", fontWeight: "600", cursor: "pointer" },

  heroButtons: { display: "flex", gap: "15px", flexWrap: "wrap", marginBottom: "35px", width: "100%" },
  primaryButton: { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "14px 28px", background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)", color: "white", borderRadius: "10px", fontSize: "15px", fontWeight: "600", textDecoration: "none", boxShadow: "0 10px 20px rgba(79, 70, 229, 0.3)", transition: "all 0.3s ease" },
  secondaryButton: { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "14px 28px", backgroundColor: "rgba(255, 255, 255, 0.1)", backdropFilter: "blur(10px)", color: "white", border: "2px solid rgba(255, 255, 255, 0.3)", borderRadius: "10px", fontSize: "15px", fontWeight: "600", textDecoration: "none", transition: "all 0.3s ease" },

  trustIndicators: { display: "flex", gap: "clamp(12px, 2.5vw, 24px)", flexWrap: "wrap" },
  trustItem: { display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#C7D2FE" },

  statsSection: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: "25px", padding: "clamp(40px, 4vw, 60px) 5%", backgroundColor: "white", maxWidth: "1400px", margin: "0 auto" },
  statCard: { padding: "30px 20px", borderRadius: "14px", backgroundColor: "white", textAlign: "center", transition: "all 0.4s ease", boxShadow: "0 6px 20px rgba(0,0,0,0.08)" },
  statNumber: { fontSize: "clamp(28px, 3vw, 40px)", fontWeight: "800", margin: "12px 0", color: "#1E293B" },
  statText: { color: "#64748B", fontSize: "15px", fontWeight: "500" },

  sectionHeader: { textAlign: "center", marginBottom: "40px", padding: "0 20px" },
  sectionTitle: { fontSize: "clamp(26px, 3.5vw, 42px)", fontWeight: "800", marginBottom: "12px", color: "#1E293B" },
  sectionSubtitle: { fontSize: "clamp(15px, 1.5vw, 16px)", color: "#64748B", maxWidth: "600px", margin: "0 auto" },

  trustSection: { padding: "clamp(50px, 4vw, 80px) 5%", backgroundColor: "#F8FAFC" },
  trustGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(230px, 100%), 1fr))", gap: "25px", maxWidth: "1200px", margin: "0 auto" },
  trustCard: { padding: "30px 25px", borderRadius: "16px", backgroundColor: "white", boxShadow: "0 8px 25px rgba(0, 0, 0, 0.05)", textAlign: "center", transition: "all 0.4s ease" },
  trustCardTitle: { fontSize: "18px", fontWeight: "700", marginBottom: "12px", color: "#1E293B" },
  trustCardDesc: { color: "#64748B", lineHeight: "1.5", fontSize: "14px" },

  categorySection: { padding: "clamp(50px, 4vw, 80px) 5%", backgroundColor: "white" },
  filterTabs: { display: "flex", justifyContent: "center", gap: "10px", marginBottom: "25px", flexWrap: "wrap", padding: "0 10px" },
  filterTab: { padding: "8px 18px", borderRadius: "50px", border: "2px solid", cursor: "pointer", fontWeight: "600", fontSize: "13px", transition: "all 0.3s ease", display: "flex", alignItems: "center", gap: "6px" },
  activeDot: { fontSize: "8px", animation: "pulse 2s infinite" },
  courseCount: { textAlign: "center", color: "#64748B", marginBottom: "25px", fontSize: "13px", fontWeight: "500" },
  courseCard: { background: "white", borderRadius: "18px", padding: "25px 20px", textAlign: "center", boxShadow: "0 6px 20px rgba(0,0,0,0.08)", transition: "all 0.4s ease", position: "relative", overflow: "hidden", maxWidth: "380px", width: "100%", minWidth: 0, margin: "0 auto" },
  courseBadge: { position: "absolute", top: "12px", right: "12px", backgroundColor: "#EF4444", color: "white", padding: "4px 10px", borderRadius: "16px", fontSize: "11px", fontWeight: "600" },
  courseTitle: { fontSize: "18px", fontWeight: "700", margin: "12px 0 8px", color: "#1E293B", overflowWrap: "anywhere" },
  categoryBadge: { display: "inline-block", padding: "5px 12px", borderRadius: "16px", fontSize: "11px", fontWeight: "600", marginBottom: "16px" },
  courseMeta: { display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "12px", marginBottom: "16px", fontSize: "12px", color: "#64748B" },
  metaItem: { display: "flex", alignItems: "center" },
  viewCourseButton: { width: "100%", padding: "10px", backgroundColor: "#F1F5F9", color: "#4F46E5", border: "none", borderRadius: "8px", fontWeight: "600", cursor: "pointer" },
  carouselControls: { display: "flex", justifyContent: "center", gap: "15px", marginTop: "25px" },
  controlButton: { width: "40px", height: "40px", borderRadius: "50%", backgroundColor: "white", border: "2px solid #E2E8F0", color: "#4F46E5", fontSize: "18px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 10px rgba(0,0,0,0.08)" },
  progressIndicator: { display: "flex", justifyContent: "center", gap: "6px", marginTop: "30px" },
  progressDot: { height: "6px", borderRadius: "3px", transition: "all 0.3s ease" },

  progressSection: { padding: "clamp(50px, 4vw, 70px) 5%", backgroundColor: "#F8FAFC" },
  progressGrid: { maxWidth: "750px", margin: "0 auto", display: "flex", flexDirection: "column", gap: "25px", width: "100%" },
  progressBarContainer: { marginBottom: "8px" },
  progressBarLabel: { display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "13px", fontWeight: "600", color: "#475569" },
  progressBarTrack: { height: "8px", backgroundColor: "#E2E8F0", borderRadius: "4px", overflow: "hidden" },
  progressBarFill: { height: "100%", borderRadius: "4px", transition: "width 1s ease-in-out" },

  categoryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: "25px", maxWidth: "1200px", margin: "0 auto" },
  categoryCard: { padding: "30px 25px", borderRadius: "16px", textAlign: "center", transition: "all 0.4s ease", position: "relative", overflow: "hidden" },
  categoryTitle: { fontSize: "18px", fontWeight: "700", margin: "12px 0 6px", color: "#1E293B" },
  categoryCourses: { color: "#64748B", fontSize: "13px", marginBottom: "16px" },
  categoryHover: { opacity: 0, transform: "translateY(8px)", transition: "all 0.3s ease", color: "#4F46E5", fontWeight: "600", fontSize: "13px" },

  features: { padding: "clamp(50px, 4vw, 80px) 5%", background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)" },
  featuresGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: "30px", maxWidth: "1200px", margin: "0 auto" },
  featureLink: { textDecoration: "none", color: "inherit", display: "block" },
  featureCard: { backgroundColor: "white", padding: "30px 25px", borderRadius: "20px", boxShadow: "0 8px 25px rgba(0, 0, 0, 0.05)", textAlign: "center", transition: "all 0.4s ease", border: "2px solid transparent", height: "100%" },
  featureIconContainer: { width: "65px", height: "65px", borderRadius: "16px", background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" },
  featureIcon: { fontSize: "30px", color: "white" },
  featureTitle: { fontSize: "20px", fontWeight: "700", marginBottom: "12px", color: "#1E293B" },
  featureDesc: { color: "#64748B", lineHeight: "1.5", marginBottom: "16px", fontSize: "14px" },
  featureStats: { display: "inline-block", padding: "5px 12px", backgroundColor: "#F1F5F9", color: "#4F46E5", borderRadius: "16px", fontSize: "13px", fontWeight: "600" },

  newsletterSection: { padding: "clamp(50px, 4vw, 80px) 5%", background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)", textAlign: "center", color: "white" },
  newsletterContent: { maxWidth: "550px", margin: "0 auto", width: "100%" },
  newsletterIcon: { fontSize: "40px", marginBottom: "16px" },
  newsletterTitle: { fontSize: "clamp(22px, 3.5vw, 32px)", fontWeight: "700", marginBottom: "12px" },
  newsletterText: { fontSize: "16px", opacity: 0.9, marginBottom: "25px", lineHeight: "1.5" },
  newsletterForm: { display: "flex", gap: "10px", marginBottom: "12px", width: "100%", flexWrap: "wrap" },
  newsletterInput: { flex: "1 1 200px", minWidth: 0, padding: "14px 18px", border: "none", borderRadius: "10px", fontSize: "16px", backgroundColor: "rgba(255, 255, 255, 0.95)" },
  newsletterButton: { padding: "14px 28px", backgroundColor: "#1E293B", color: "white", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: "600", cursor: "pointer" },
  newsletterDisclaimer: { fontSize: "13px", opacity: 0.8 },

  about: { padding: "clamp(50px, 4vw, 80px) 5%", backgroundColor: "white" },
  aboutContainer: { display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "40px", maxWidth: "900px", margin: "0 auto" },
  aboutImageContainer: { position: "relative", flexShrink: 0 },
  aboutImage: { width: "240px", height: "240px", borderRadius: "20px", objectFit: "cover", boxShadow: "0 15px 30px rgba(0, 0, 0, 0.12)", maxWidth: "100%" },
  aboutBadge: { position: "absolute", bottom: "-10px", right: "-10px", backgroundColor: "#4F46E5", color: "white", padding: "6px 16px", borderRadius: "16px", fontSize: "13px", fontWeight: "600", boxShadow: "0 6px 15px rgba(79, 70, 229, 0.25)" },
  aboutContent: { flex: 1, minWidth: "260px" },
  aboutName: { fontSize: "clamp(22px, 3vw, 28px)", fontWeight: "800", marginBottom: "6px", color: "#1E293B" },
  aboutRole: { fontSize: "16px", color: "#4F46E5", marginBottom: "20px", fontWeight: "600" },
  aboutBio: { fontSize: "15px", color: "#475569", lineHeight: "1.6", marginBottom: "25px" },
  aboutStats: { display: "flex", gap: "25px", flexWrap: "wrap" },
  stat: { textAlign: "center" },
  // Distinct from `statNumber` above, which sizes the animated hero stat
  // cards. Both were called `statNumber`, so this one silently overwrote the
  // other and the hero figures rendered at 24px instead of their clamp().
  aboutStatNumber: { display: "block", fontSize: "24px", fontWeight: "800", color: "#4F46E5", marginBottom: "4px" },
  statLabel: { display: "block", fontSize: "13px", color: "#64748B" },

  faqSection: { padding: "clamp(50px, 4vw, 80px) 5%", backgroundColor: "#F8FAFC" },
  faqGrid: { display: "grid", gap: "16px", maxWidth: "800px", margin: "0 auto" },
  faqItem: { backgroundColor: "white", padding: "25px", borderRadius: "14px", boxShadow: "0 8px 25px rgba(0, 0, 0, 0.04)" },
  faqQuestion: { fontSize: "18px", fontWeight: "700", color: "#1E293B", marginBottom: "10px" },
  faqAnswer: { fontSize: "15px", color: "#64748B", lineHeight: "1.6" },

  ctaSection: { padding: "clamp(50px, 4vw, 80px) 5%", textAlign: "center", background: "linear-gradient(135deg, #1E1B4B 0%, #312E81 100%)", color: "white" },
  ctaContent: { maxWidth: "750px", margin: "0 auto", width: "100%" },
  ctaTitle: { fontSize: "clamp(28px, 4vw, 40px)", fontWeight: "800", marginBottom: "16px" },
  ctaText: { fontSize: "16px", opacity: 0.9, marginBottom: "32px", maxWidth: "550px", margin: "0 auto 32px auto", lineHeight: "1.5" },
  ctaButtons: { display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap" },
  ctaPrimaryButton: { padding: "16px 32px", backgroundColor: "white", color: "#4F46E5", borderRadius: "10px", fontSize: "15px", fontWeight: "600", textDecoration: "none", boxShadow: "0 8px 20px rgba(255, 255, 255, 0.15)", transition: "all 0.3s ease" },
  ctaSecondaryButton: { padding: "16px 32px", backgroundColor: "transparent", color: "white", border: "2px solid rgba(255, 255, 255, 0.3)", borderRadius: "10px", fontSize: "15px", fontWeight: "600", textDecoration: "none", transition: "all 0.3s ease" },

  scrollButton: { position: "fixed", bottom: "25px", right: "25px", width: "50px", height: "50px", backgroundColor: "#4F46E5", color: "white", border: "none", borderRadius: "50%", cursor: "pointer", fontSize: "18px", boxShadow: "0 8px 20px rgba(79, 70, 229, 0.25)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
};

export default Home;
